import React, { useState, Fragment } from 'react';

import LoadingButton from '@atlaskit/button/loading-button';
import Button from '@atlaskit/button/standard-button';
import Form, { Field, ErrorMessage } from '@atlaskit/form';
import Modal, {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  ModalTransition,
} from '@atlaskit/modal-dialog';
import TextArea from '@atlaskit/textarea';
import TextField from '@atlaskit/textfield';

import { useIntl } from 'src/hooks/intl';

import {
  CreateTagRequestData,
  CreateTagResponseData,
} from '../hooks/tag-hooks';

import messages from './commit-page.i18n';

export const RenderAddModal = (
  createTag: (data: CreateTagRequestData) => Promise<CreateTagResponseData>,
  addModal: boolean,
  setAddModal: (state: boolean) => void
): JSX.Element => {
  const intl = useIntl();

  const [errorMessage, setErrorMessage] = useState('');

  const onSubmit = async (data: CreateTagRequestData) => {
    const { success, error } = await createTag(data);
    if (success) {
      setAddModal(false);
    } else {
      setErrorMessage(error.message);
    }
  };

  return (
    <ModalTransition>
      {addModal && (
        <Modal onClose={() => setAddModal(false)}>
          <ModalHeader>
            <ModalTitle>{intl.formatMessage(messages.createTag)}</ModalTitle>
          </ModalHeader>
          <Form<CreateTagRequestData> onSubmit={onSubmit}>
            {({ formProps, submitting }) => (
              <form {...formProps}>
                <ModalBody>
                  {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
                  <Field
                    aria-required
                    name="name"
                    label={intl.formatMessage(messages.tagName)}
                    isRequired
                  >
                    {({ fieldProps, error, valid }) => (
                      <Fragment>
                        <TextField autoComplete="off" {...fieldProps} />
                        {!valid && error && (
                          <ErrorMessage>{error}</ErrorMessage>
                        )}
                      </Fragment>
                    )}
                  </Field>
                  <Field
                    name="description"
                    label={intl.formatMessage(messages.tagDescription)}
                  >
                    {({ fieldProps }: any) => (
                      <Fragment>
                        <TextArea {...fieldProps} />
                      </Fragment>
                    )}
                  </Field>
                </ModalBody>
                <ModalFooter>
                  <Button
                    appearance="subtle"
                    onClick={() => setAddModal(false)}
                  >
                    {intl.formatMessage(messages.tagCancel)}
                  </Button>
                  <LoadingButton
                    appearance="primary"
                    type="submit"
                    isLoading={submitting}
                    autoFocus
                  >
                    {intl.formatMessage(messages.createTag)}
                  </LoadingButton>
                </ModalFooter>
              </form>
            )}
          </Form>
        </Modal>
      )}
    </ModalTransition>
  );
};
